import React, { useEffect, useMemo, useRef } from 'react';
import clsx from 'clsx';
import { Chart } from '@antv/g2';
import {
	COMPARISON_SERIES_CURRENT,
	COMPARISON_SERIES_FIELD,
	COMPARISON_SERIES_PREVIOUS,
} from '../../utils/AnalyticsComparisonUtils';

const DEFAULT_HEIGHT = 400;
const DEFAULT_BAR_SIZE = 40;
const DEFAULT_BAR_SIZE_COMPARISON = 40;
/** Target gap in pixels between the previous-period and current-period bars in each pair. */
const COMPARISON_INTRA_PAIR_GAP_PX = 8;
/**
 * G2 dodge `marginRatio` is expressed as `gapPx / barWidthPx`, so 8 / 40 = 0.2 gets us the
 * precise 8px gap the design specifies between the dashed (previous) and solid (current) bars.
 */
const COMPARISON_DODGE_MARGIN_RATIO = COMPARISON_INTRA_PAIR_GAP_PX / DEFAULT_BAR_SIZE_COMPARISON;

export type PriorityStackedBarChartProps = {
	/** Flat row list: one entry per (xField × stackField × series). Comparison mode also needs
	 *  the `COMPARISON_SERIES_FIELD` value set on every row. */
	data: any[];
	xField?: string;
	yField?: string;
	stackField?: string;
	/** Map of stack value → hex color. Drives both bar fills and the priority legend. */
	stackColorMap: Record<string, string>;
	stackOrder?: string[];
	hasComparison: boolean;
	axisXTitle?: string;
	axisYTitle?: string;
	formatYAxisLabel?: (val: any) => string;
	renderTooltip?: (title: string, items: any[]) => string;
	height?: number;
	barSize?: number;
	/** Unique-per-chart prefix for the injected SVG <pattern> ids. Default is fine unless two
	 *  priority charts share the same DOM (then provide distinct values). */
	patternIdPrefix?: string;
};

/**
 * Strip everything that isn't `[a-zA-Z0-9]` from the priority name so the resulting pattern id
 * is always a valid SVG fragment identifier — priority names like "Normal - 48 Hrs" or
 * "Emergency - 4 Hrs" otherwise produced ids with leading underscores and double-dashes that
 * some SVG renderers refused to resolve, falling back to a black fill.
 */
const sanitizeStackIdSegment = (raw: any): string => String(raw).replace(/[^a-zA-Z0-9]+/g, '');

const PriorityStackedBarChart = ({
	data,
	xField = 'monthLabel',
	yField = 'value',
	stackField = 'priority',
	stackColorMap,
	stackOrder,
	hasComparison,
	axisXTitle = 'Month',
	axisYTitle = 'Work Orders Created',
	formatYAxisLabel,
	renderTooltip,
	height = DEFAULT_HEIGHT,
	barSize,
	patternIdPrefix = 'priority-hatch',
}: PriorityStackedBarChartProps) => {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const chartRef = useRef<Chart | null>(null);

	const patternEntries = useMemo(
		() =>
			Object.keys(stackColorMap).map((stackValue) => ({
				stackValue,
				color: stackColorMap[stackValue],
				patternId: `${patternIdPrefix}-${sanitizeStackIdSegment(stackValue)}`,
			})),
		[stackColorMap, patternIdPrefix]
	);

	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		const effectiveBarSize =
			typeof barSize === 'number'
				? barSize
				: hasComparison
				? DEFAULT_BAR_SIZE_COMPARISON
				: DEFAULT_BAR_SIZE;

		const chart = new Chart({
			container,
			autoFit: true,
			height,
			// Same bottom padding in both modes so the comparison labels sit at the same
			// vertical distance from the axis as the single-mode tick labels.
			padding: [24, 40, 80, 70],
			renderer: 'svg',
		} as any);

		chart.data(data || []);

		chart.scale(yField, { nice: true, min: 0 });
		if (stackOrder && stackOrder.length > 0) {
			chart.scale(stackField, { type: 'cat', values: stackOrder });
		}
		if (hasComparison) {
			// Force the dodge order so the dashed (previous) bar sits to the LEFT of the solid
			// (current) bar inside each month group. G2 dodges in scale-value order, so listing
			// previous before current here pins the layout.
			chart.scale(COMPARISON_SERIES_FIELD, {
				type: 'cat',
				values: [COMPARISON_SERIES_PREVIOUS, COMPARISON_SERIES_CURRENT],
			});
		}

		chart.axis(xField, {
			title: {
				text: axisXTitle,
				style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' } as any,
				offset: 60,
			},
			tickLine: null as any,
			// Hide the shared (centered) x-axis label in comparison mode — the per-bar paired
			// labels we stamp below each dodged bar replace it. In single mode keep the
			// standard rotated month label.
			label: hasComparison
				? (null as any)
				: ({
						style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' } as any,
						autoRotate: false,
						rotate: -Math.PI / 4,
						offset: 18,
				  } as any),
		});

		chart.axis(yField, {
			title: axisYTitle
				? {
						text: axisYTitle,
						style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' } as any,
						offset: 50,
				  }
				: null,
			label: {
				formatter: formatYAxisLabel as any,
				style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' } as any,
			},
			grid: {
				line: { style: { stroke: '#e8e8e8', lineDash: [4, 4] } as any },
			} as any,
		});

		chart.legend(false);

		const dashedCrosshairs = {
			type: 'x',
			line: { style: { stroke: 'rgba(85, 85, 85, 0.8)', lineWidth: 1, lineDash: [4, 4] } },
		};

		if (renderTooltip) {
			chart.tooltip({
				shared: true,
				showTitle: true,
				showMarkers: false,
				showCrosshairs: true,
				crosshairs: dashedCrosshairs as any,
				customContent: (title: string, items: any[]) => renderTooltip(title, items || []),
				domStyles: {
					'g2-tooltip': {
						backgroundColor: '#ffffff',
						boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
						borderRadius: '4px',
						padding: '0',
					},
				} as any,
			});
		} else {
			chart.tooltip({
				shared: true,
				showMarkers: false,
				showCrosshairs: true,
				crosshairs: dashedCrosshairs as any,
			});
		}

		const interval = chart
			.interval()
			.position(`${xField}*${yField}`)
			.color(stackField, (stackValue: string) => stackColorMap[stackValue] || '#888888')
			.size(effectiveBarSize)
			.adjust(
				hasComparison
					? [
							{
								type: 'dodge',
								dodgeBy: COMPARISON_SERIES_FIELD,
								marginRatio: COMPARISON_DODGE_MARGIN_RATIO,
							},
							{ type: 'stack' },
					  ]
					: [{ type: 'stack' }]
			);

		if (hasComparison) {
			interval.style(
				`${COMPARISON_SERIES_FIELD}*${stackField}`,
				(series: string, stackValue: string) => {
					if (series === COMPARISON_SERIES_PREVIOUS) {
						const patternId = `${patternIdPrefix}-${sanitizeStackIdSegment(stackValue)}`;
						return {
							fill: `url(#${patternId})`,
							stroke: stackColorMap[stackValue] || '#888888',
							lineWidth: 1,
						};
					}
					return {};
				}
			);

			// Per-bar paired month label (matches lifecycle SpendBarChart / ColumnLineComboChart).
			// The interval.label callback runs once per data row — and our data has one row per
			// (month × series × priority), so we'd otherwise stamp 1 label per stack segment.
			// To get exactly ONE label per dodged bar, render only when the priority matches
			// the bottom-most stack segment (which sits at y=0, so position:'bottom' places the
			// label cleanly below the x-axis).
			// G2's `stack` adjust reverses by default for vertical bars, so the FIRST data row
			// per (x, series) group lands at the visual TOP of the stack and the LAST row lands
			// at the visual BOTTOM. We want labels anchored to the bottom-most segment so that
			// `position: 'bottom'` resolves to y=0 (the x-axis line) and the negative offset
			// pushes the label below into the chart's bottom padding — matching the lifecycle
			// SpendBarChart `offset: -12` convention.
			const labelAnchorStackValue =
				stackOrder && stackOrder.length > 0 ? stackOrder[stackOrder.length - 1] : null;
			if (labelAnchorStackValue) {
				interval.label(
					`${stackField}*${COMPARISON_SERIES_FIELD}*previousMonthLabel*${xField}`,
					(priority: any, series: any, prevMonth: any, curMonth: any) => {
						if (priority !== labelAnchorStackValue) {
							return { content: '' } as any;
						}
						const text = series === COMPARISON_SERIES_PREVIOUS ? prevMonth || '' : curMonth || '';
						return {
							content: text,
							position: 'bottom',
							// Same magnitude the single-mode tick label uses (axis label offset: 18),
							// negated because G2 measures label offset from `position: 'bottom'`
							// toward the chart center. Keeps the paired labels hugging the axis
							// line like the single-mode tick labels do.
							offset: -18,
							autoRotate: false,
							rotate: -Math.PI / 4,
							style: {
								fontSize: 12,
								fill: 'rgba(0,0,0,0.45)',
								textAlign: 'end',
								textBaseline: 'middle',
							} as any,
						} as any;
					}
				);
			}
		}

		chart.render();
		chartRef.current = chart;

		return () => {
			chart.destroy();
			chartRef.current = null;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, hasComparison, height, JSON.stringify(stackColorMap), JSON.stringify(stackOrder)]);

	const g2ChartScopeClass = hasComparison
		? 'analytics-g2-chart--comparison'
		: 'analytics-g2-chart--single';

	return (
		<div className={clsx('analytics-g2-chart w-full min-w-0', g2ChartScopeClass)}>
			{/*
			 * Patterns live in their own hidden inline SVG, declared by React BEFORE the G2 chart
			 * mounts. SVG fragment identifiers (`url(#id)`) resolve across all SVGs in the same
			 * document, so the chart can paint the previous-period bars with these patterns
			 * without any timing race or `<defs>` injection into G2's own svg.
			 *
			 * Matches the approach in the feasibility-test reference: rotate(-45) pattern, 8x8
			 * tile with a white background and a colored vertical stripe (stroke-width 10 so the
			 * line covers ~75% of the tile width before rotation, producing the diagonal hatch).
			 */}
			<svg width="0" height="0" style={{ position: 'absolute', pointerEvents: 'none' }}>
				<defs>
					{patternEntries.map(({ patternId, color }) => (
						<pattern
							key={patternId}
							id={patternId}
							patternUnits="userSpaceOnUse"
							width={8}
							height={8}
							patternTransform="rotate(-45)"
						>
							{/*
							 * Figma calls for the previous-period bars to read as the priority's
							 * COLOR overlaid with thin WHITE diagonal stripes (not the inverse).
							 * Paint the tile in the priority color first, then a 8px-wide white
							 * line that becomes the visible stripe once the 8x8 tile is rotated
							 * -45°. The colored area is dominant; the white slits are thin.
							 */}
							<rect width="8" height="8" fill={color} />
							<line x1="0" y1="0" x2="0" y2="8" stroke="#ffffff" strokeWidth={8} />
						</pattern>
					))}
				</defs>
			</svg>
			<div ref={containerRef} style={{ width: '100%', height }} />
		</div>
	);
};

export default PriorityStackedBarChart;
